import React, { useState } from 'react';
import { Modal, Input, Button, Form, message, Grid, Row, Col, Divider } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; 

// 扩展表单值类型，添加备注字段
interface InquiryFormValues {
    quantity: number;
    targetPrice: number;
    contactNumber: string;
    name: string;
    companyName: string;
    email: string;
    remark: string;
    partNumberInput?: string; 
}

interface InquiryModalProps {
    part_number?: string;
}

const InquiryModal: React.FC<InquiryModalProps> = (props) => {
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.md; 
    const { t } = useTranslation();

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleOk = () => {
        form.validateFields().then(async (values) => { 
            try {
                await sendInquiryEmail(values);
                setIsModalVisible(false);
                form.resetFields();
                message.success(t('inquiry.sentSuccessfully'));
            } catch (error) {
                console.error('发送询价单失败:', error);
                message.error(t('inquiry.sentFailed'));
            }
        }).catch(errorInfo => {
            console.log('Validation failed:', errorInfo);
        });
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const sendInquiryEmail = async (values: InquiryFormValues) => {
        const partNumber = props.part_number || values.partNumberInput || '-';
        const emailBody = `
            物料编号: ${partNumber}\n
            数量: ${values.quantity}\n
            目标价格: ${values.targetPrice || '-'}\n
            备注: ${values.remark || '-'}\n
            联系电话: ${values.contactNumber}\n
            姓名: ${values.name || '-'}\n
            公司名: ${values.companyName || '-'}\n
            邮箱地址: ${values.email || '-'}\n
        `;
        const requestData = {
            to: 'sales@eetl-evo.com',
            subject: '询价单',
            body: emailBody
        };

        try {
            await axios.post(`${API_BASE}/inquiry/send-email/`, requestData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            message.success(t('inquiry.emailSent'));
        } catch (error) {
            console.error('发送邮件请求失败:', error);
            throw error;
        }
    };

    const validateContactInfo = (_: unknown, value: string) => {
        const emailValue = form.getFieldValue('email');
        if (!value && !emailValue) {
            return Promise.reject(new Error(t('inquiry.contactOrEmailRequired')));
        }
        return Promise.resolve();
    };

    return (
        <>
            {props.part_number ? (
                <Button
                    type="primary"
                    size="large"
                    style={{
                        width: 160,
                        marginTop: 16,
                        boxShadow: '0 2px 6px rgba(24,144,255,0.3)'
                    }}
                    onClick={showModal}
                >
                    <span style={{ fontSize: 16 }}>{t('inquiry.inquireNow')}</span>
                </Button>
            ) : (
                <Button
                    type="primary"
                    className='button-normal'
                    shape="round"
                    style={{ width: '160px', height: '46px' }}
                    onClick={showModal}
                >
                    {t('footer.productConsultation.consultButton')}
                </Button>
            )}
            <Modal
                title={props.part_number ? t('inquiry.inquiryFormTitle') : t('inquiry.sampleConsultation')}
                open={isModalVisible}
                onCancel={handleCancel}
                width={800}
                centered
                footer={[
                    <Button key="cancel" size="large" onClick={handleCancel}>
                        {t('inquiry.cancel')}
                    </Button>,
                    <Button key="submit" className='ml-20' type="primary" size="large" onClick={handleOk}>
                        {t('inquiry.submit')}
                    </Button>,
                ]}
            >
	{isModalVisible && (
                <Form
                    form={form} 
                    layout="horizontal"
                    labelAlign="left"
                    className='inquiry-form pt-20 pb-20'
                >
                    {!props.part_number && (
                        <Form.Item
                            name="partNumberInput" 
                            label={t('inquiry.partNumber')}
                            style={{ marginBottom: 16 }}
                        >
                            <Input style={{ height: 40, fontSize: 14 }} />
                        </Form.Item>
                    )}
                    {props.part_number || form.getFieldValue('partNumberInput') ? (
                        <Form.Item label={t('inquiry.partNumber')}>
                            <span style={{ fontSize: 16, color: '#333' }}>
                                {props.part_number || form.getFieldValue('partNumberInput')}
                            </span>
                        </Form.Item>
                    ) : null}
                    {!isMobile ? (
                        <Row gutter={32}>
                            <Col span={12}>
                                <Form.Item
                                    name="quantity" 
                                    label={t('inquiry.quantity')}
                                    rules={[{ required: true, message: t('inquiry.quantityRequired') }]}
                                    extra={<small className='text-info'>{t('inquiry.quantityHint')}</small>}
                                >
                                    <Input type="number" size="large" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="targetPrice" 
                                    label={t('inquiry.targetPrice')}
                                    extra={<small className='text-info'>{t('inquiry.targetPriceHint')}</small>}
                                >
                                    <Input type="number" addonBefore="$" size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Form.Item
                                name="quantity" 
                                label={t('inquiry.quantity')}
                                rules={[{ required: true, message: t('inquiry.quantityRequired') }]}
                                style={{ marginBottom: 16 }}
                            >
                                <Input type="number" style={{ height: 40, fontSize: 14 }} />
                            </Form.Item>
                            <Form.Item
                                name="targetPrice" 
                                label={t('inquiry.targetPrice')}
                                style={{ marginBottom: 16 }}
                            >
                                <Input type="number" style={{ height: 40, fontSize: 14 }} />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item
                        name="remark" 
                        label={t('inquiry.remark')}
                        style={{ marginBottom: 8 }}
                    >
                        <Input.TextArea placeholder={t('inquiry.remarkPlaceholder')} rows={4} style={{ fontSize: 14 }} />
                        </Form.Item>
                    <Divider />
                    <Form.Item
                        label=""
                    >
                        <div>
                            {t('inquiry.contactInfoHint')}
                        </div>
                    </Form.Item>
                    <Form.Item
                        name="contactNumber" 
                        label={t('inquiry.contactNumber')}
                        rules={[
                            { required: false },
                            {
                                validator: validateContactInfo
                            }
                        ]}
                        style={{ marginBottom: 16 }}
                    >
                        <Input style={{ height: 40, fontSize: 14 }} />
                    </Form.Item>
                    <Form.Item
                        name="name" 
                        label={t('inquiry.name')}
                        style={{ marginBottom: 16 }}
                    >
                        <Input style={{ height: 40, fontSize: 14 }} />
                    </Form.Item>
                    <Form.Item
                        name="companyName" 
                        label={t('inquiry.companyName')}
                        style={{ marginBottom: 16 }}
                    >
                        <Input style={{ height: 40, fontSize: 14 }} />
                    </Form.Item>
                    <Form.Item
                        name="email" 
                        label={t('inquiry.email')}
                        rules={[{ type: 'email', message: t('inquiry.validEmailRequired') }, { validator: validateContactInfo }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Input style={{ height: 40, fontSize: 14 }} />
                    </Form.Item>
                </Form>
)}
            </Modal>
        </>
    );
};

export default InquiryModal;